import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Edit3, RefreshCw, X, Loader2, Star } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, Input, Textarea } from './shared';
import { useToast } from '../../hooks/use-toast';

const BLANK = { name: '', company: '', role: '', rating: 5, msg: '', order: 0 };

export default function AdminReviews() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/reviews');
      setItems(res.data || []);
    } catch (err) { toast({ title: 'Failed to load', description: formatApiError(err) }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name?.trim() || !editing.msg?.trim()) return toast({ title: 'Name and message are required' });
    setSaving(true);
    try {
      const body = {
        name: editing.name.trim(),
        company: editing.company || '',
        role: editing.role || '',
        rating: Number(editing.rating) || 5,
        msg: editing.msg,
        order: Number(editing.order) || 0,
      };
      if (editing.id) await api.patch(`/api/admin/reviews/${editing.id}`, body);
      else await api.post('/api/admin/reviews', body);
      toast({ title: 'Saved' });
      setEditing(null); load();
    } catch (err) { toast({ title: 'Save failed', description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try { await api.delete(`/api/admin/reviews/${id}`); load(); toast({ title: 'Deleted' }); }
    catch (err) { toast({ title: 'Delete failed', description: formatApiError(err) }); }
  };

  return (
    <>
      <PageHeader
        title="Customer Reviews"
        subtitle="Testimonials shown on the public reviews page"
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn onClick={() => setEditing({ ...BLANK })}><Plus size={14} /> Add review</Btn>
          </div>
        }
      />

      <AdminCard>
        {loading ? <Loading /> : items.length === 0 ? <Empty /> : (
          <div className="grid md:grid-cols-2 gap-3">
            {items.map((r) => (
              <div key={r.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-white truncate">{r.name}</p>
                    <p className="text-xs text-slate-400 truncate">{r.role || ''}{r.role && r.company ? ' · ' : ''}{r.company || ''}</p>
                  </div>
                  <div className="flex gap-0.5 text-amber-400">{Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} size={12} fill="currentColor" />)}</div>
                </div>
                <p className="text-sm text-slate-200 mt-2 line-clamp-3">{r.msg}</p>
                <div className="flex gap-1 mt-3">
                  <Btn size="sm" variant="ghost" onClick={() => setEditing(r)}><Edit3 size={12} /> Edit</Btn>
                  <Btn size="sm" variant="danger" onClick={() => remove(r.id)}><Trash2 size={12} /></Btn>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminCard>

      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => !saving && setEditing(null)}>
          <div className="flex-1 bg-black/60" />
          <aside className="w-full max-w-lg bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">{editing.id ? 'Edit review' : 'New review'}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <Input label="Name *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Role" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} />
                <Input label="Company" value={editing.company} onChange={(e) => setEditing({ ...editing, company: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Rating (1-5)" type="number" min="1" max="5" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} />
                <Input label="Order" type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              </div>
              <Textarea label="Message *" rows={5} value={editing.msg} onChange={(e) => setEditing({ ...editing, msg: e.target.value })} />
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save'}</Btn>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
